import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  ACTIVITY_CATEGORY_META,
  ALL_ACTIVITY_CATEGORY_SLUGS,
  ActivityApiService,
  AuthService,
  IActivityCategoryMeta,
  ListingAddressGeocodingService,
  ToastService,
  buildActivityLocationSlugSegments,
  downscalePhoto,
  inferActivityCategorySlug,
  measureDataUrl,
} from '@cnt-workspace/data-access';
import { CntSymbolDirective, FooterComponent, NavbarComponent } from '@cnt-workspace/ui';
import { PhoneMaskDirective } from '@curbnturf/cnt-masks/src/lib/phone/phone-mask.directive';
import {
  ActivityLocation,
  HelperFunctions,
  IActivityLocation,
  IAutocompleteSuggestion,
  IPhoto,
} from '@curbnturf/entities';
import { firstValueFrom } from 'rxjs';
import { ActivityLocationMapComponent } from '../activity-location-map/activity-location-map.component';

/** All 23 real category/activity flags an `IActivityLocation` can carry —
 *  everything in `ACTIVITY_CATEGORY_META` except the generic `activity`
 *  fallback slug, which has no backing field. */
const CATEGORY_OPTIONS: IActivityCategoryMeta[] = ALL_ACTIVITY_CATEGORY_SLUGS.filter((slug) => slug !== 'activity').map(
  (slug) => ACTIVITY_CATEGORY_META[slug],
);

@Component({
  selector: 'cnt-activity-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NavbarComponent,
    FooterComponent,
    ActivityLocationMapComponent,
    PhoneMaskDirective,
    CntSymbolDirective,
  ],
  templateUrl: './activity-form.component.html',
})
export class ActivityFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private activityApi = inject(ActivityApiService);
  private auth = inject(AuthService);
  private toasts = inject(ToastService);
  private geocoding = inject(ListingAddressGeocodingService);

  readonly categoryOptions = CATEGORY_OPTIONS;

  id?: number;
  editing = false;
  loading = false;
  saving = false;

  flags: Record<string, boolean> = {};
  customActivity = '';
  name = '';
  description = '';
  address = '';
  city = '';
  state = '';
  zipcode = '';
  lat?: number;
  lng?: number;
  phone = '';
  url = '';
  hidden = true;
  locating = false;

  /** Existing server-side photos (edit mode) — indices marked in
   *  `removedPhotoIndexes` are deleted on save. */
  existingPhotos: IPhoto[] = [];
  removedPhotoIndexes = new Set<number>();
  /** Newly-picked photos, downscaled to data URLs, uploaded on save. */
  newPhotos: string[] = [];

  suggestions: IAutocompleteSuggestion[] = [];

  get hasAtLeastOneCategory(): boolean {
    return CATEGORY_OPTIONS.some((c) => this.flags[c.field as string]) || !!this.customActivity.trim();
  }

  photoUrl(photo: IPhoto): string {
    return HelperFunctions.getPhotoUrlOriginal(photo);
  }

  ngOnInit(): void {
    for (const c of CATEGORY_OPTIONS) this.flags[c.field as string] = false;

    const idRaw = this.route.snapshot.paramMap.get('id');
    if (idRaw) {
      this.id = Number(idRaw);
      this.editing = true;
      this.loading = true;
      this.activityApi.fetch(this.id).subscribe({
        next: (data) => this.applyExisting(data),
        error: () => {
          this.loading = false;
          this.toasts.error('Could not load this point of interest.');
        },
      });
    }
  }

  private applyExisting(data: IActivityLocation): void {
    const activity = new ActivityLocation(data);
    this.loading = false;
    for (const c of CATEGORY_OPTIONS) this.flags[c.field as string] = !!activity[c.field as keyof IActivityLocation];
    this.customActivity = activity.customActivity ?? '';
    this.name = activity.name ?? '';
    this.description = activity.description ?? '';
    this.address = activity.address?.address ?? '';
    this.city = activity.address?.city ?? '';
    this.state = activity.address?.state ?? '';
    this.zipcode = activity.address?.zipcode ?? '';
    this.lat = activity.coordinates?.lat;
    this.lng = activity.coordinates?.lon;
    this.phone = activity.phone ?? '';
    this.url = activity.url ?? '';
    this.hidden = activity.hidden ?? true;
    this.existingPhotos = activity.photos ?? [];
  }

  onStreetInput(term: string): void {
    if (term.trim().length < 6) {
      this.suggestions = [];
      return;
    }
    this.geocoding.autocomplete(term).subscribe((items) => (this.suggestions = items));
  }

  selectSuggestion(s: IAutocompleteSuggestion): void {
    const addr = this.geocoding.suggestionToAddress(s);
    this.address = addr.address;
    this.city = addr.city;
    this.state = addr.state;
    this.zipcode = addr.zipcode;
    this.suggestions = [];
    this.geocoding.geocode(this.geocoding.suggestionToString(s)).subscribe((pos) => {
      if (pos) {
        this.lat = pos.lat;
        this.lng = pos.lng;
      }
    });
  }

  onMapPositionChange(pos: { lat: number; lng: number }): void {
    this.lat = pos.lat;
    this.lng = pos.lng;
  }

  useMyLocation(): void {
    if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
      this.toasts.error("Location isn't available on this device.");
      return;
    }
    this.locating = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.lat = +pos.coords.latitude.toFixed(6);
        this.lng = +pos.coords.longitude.toFixed(6);
        this.locating = false;
      },
      () => {
        this.locating = false;
        this.toasts.error('Could not determine your location.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  async onPhotosPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    for (const file of files) {
      try {
        this.newPhotos.push(await downscalePhoto(file));
      } catch {
        this.toasts.error(`Could not process ${file.name}.`);
      }
    }
  }

  removeExistingPhoto(index: number): void {
    this.removedPhotoIndexes.add(index);
  }

  removeNewPhoto(index: number): void {
    this.newPhotos.splice(index, 1);
  }

  private buildPayload(): IActivityLocation {
    const payload: IActivityLocation = {
      ...this.flags,
      customActivity: this.customActivity.trim() || undefined,
      name: this.name.trim() || undefined,
      description: this.description.trim() || undefined,
      address: { address: this.address, city: this.city, state: this.state, zipcode: this.zipcode },
      coordinates: { lat: this.lat ?? 0, lon: this.lng ?? 0 },
      phone: this.phone.trim() || undefined,
      url: this.url.trim() || undefined,
      hidden: this.hidden,
      userId: this.auth.currentUser?.id,
    };
    if (this.id) payload.id = this.id;
    return payload;
  }

  async save(): Promise<void> {
    if (!this.hasAtLeastOneCategory || this.saving) return;

    if (this.hidden === false) {
      const confirmed = isPlatformBrowser(this.platformId)
        ? window.confirm(
            'Your point of interest will be reviewed by a moderator within 3 business days before it appears publicly. Continue?',
          )
        : true;
      if (!confirmed) return;
    }

    this.saving = true;
    try {
      const payload = this.buildPayload();
      const saved = this.id
        ? await firstValueFrom(this.activityApi.update(this.id, payload))
        : await firstValueFrom(this.activityApi.create(payload));
      const savedId = saved.id!;

      // Reconcile removed photos in reverse index order so earlier removals
      // don't shift the indices of ones not yet processed.
      const removed = [...this.removedPhotoIndexes].sort((a, b) => b - a);
      for (const index of removed) {
        try {
          await firstValueFrom(this.activityApi.removeImage(savedId, index));
        } catch {
          this.toasts.error(`Photo ${index + 1} failed to remove.`);
        }
      }

      for (const dataUrl of this.newPhotos) {
        try {
          const { width, height } = await measureDataUrl(dataUrl);
          const blob = await (await fetch(dataUrl)).blob();
          const form = new FormData();
          form.append('files', blob, 'photo.jpg');
          form.append('caption', '');
          form.append('cropLeft', '0');
          form.append('cropTop', '0');
          form.append('cropRight', String(width));
          form.append('cropBottom', String(height));
          form.append('zoom', '1');
          form.append('rotation', '0');
          await firstValueFrom(this.activityApi.uploadImage(savedId, form));
        } catch {
          this.toasts.error('A photo failed to upload — the rest of your point of interest was saved.');
        }
      }

      this.toasts.success(this.editing ? 'Point of interest updated.' : 'Point of interest submitted.');
      const slug = inferActivityCategorySlug(payload);
      const url = buildActivityLocationSlugSegments(slug, { ...payload, id: savedId }).join('/');
      this.router.navigateByUrl(url);
    } catch {
      this.toasts.error('Could not save your point of interest — please try again.');
    } finally {
      this.saving = false;
    }
  }
}
